import { motion } from "framer-motion";
import { ArrowUpRight, MapPin } from "lucide-react";
import { FadeUp } from "./Reveal";
import { IMAGES } from "@/lib/brand";

const JOBS = [
  {
    id: "east-side-front",
    title: "Front Yard Transformation",
    service: "Landscaping",
    area: "East Side",
    year: "2025",
    image: IMAGES.jobFront,
    span: "md:col-span-8 md:row-span-2",
  },
  {
    id: "side-yard",
    title: "Side Yard Install",
    service: "Mulch · Sod · Plants",
    area: "San Antonio",
    year: "2025",
    image: IMAGES.jobSide,
    span: "md:col-span-4",
  },
  {
    id: "sod-install",
    title: "Fresh Sod Install",
    service: "Lawn Care",
    area: "San Antonio",
    year: "2025",
    image: IMAGES.jobSod,
    span: "md:col-span-4",
  },
  {
    id: "backyard-maintain",
    title: "Backyard Maintenance",
    service: "Lawn Care",
    area: "San Antonio",
    year: "2025",
    image: IMAGES.jobBackyard,
    span: "md:col-span-6",
  },
  {
    id: "rural-lot",
    title: "Rural Lot Mow",
    service: "Lawn Care",
    area: "Surrounding Areas",
    year: "2025",
    image: IMAGES.jobLot,
    span: "md:col-span-6",
  },
];

function JobCard({ job, index, big }) {
  return (
    <motion.a
      href="#quote"
      data-testid={`job-card-${job.id}`}
      whileHover="hover"
      initial="rest"
      animate="rest"
      className="group relative block overflow-hidden bg-[#0E2A1A] w-full h-full"
      style={{ minHeight: big ? "640px" : "360px" }}
    >
      <motion.div
        variants={{ rest: { scale: 1 }, hover: { scale: 1.06 } }}
        transition={{ duration: 1.4, ease: [0.22, 1, 0.36, 1] }}
        className="absolute inset-0"
      >
        <img
          src={job.image}
          alt={job.title}
          loading="lazy"
          className="w-full h-full object-cover"
        />
      </motion.div>

      {/* Vignette */}
      <div className="absolute inset-0 bg-gradient-to-t from-[#111613]/90 via-[#111613]/20 to-transparent" />

      {/* Top row */}
      <div className="absolute top-5 left-5 right-5 flex items-start justify-between z-10">
        <span className="font-mono-sm text-[#FFFFFF]/80">
          <span className="text-[#DA7B3C]">
            N°{String(index + 1).padStart(2, "0")}
          </span>
        </span>
        <span className="font-mono-sm text-[#111613] bg-[#FFFFFF] px-3 py-1 rounded-full">
          {job.service}
        </span>
      </div>

      {/* Bottom content */}
      <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8 z-10 text-[#FFFFFF]">
        <motion.div
          variants={{ rest: { y: 0 }, hover: { y: -6 } }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        >
          <h3
            className={`font-display tracking-[-0.02em] leading-[0.95] ${
              big
                ? "text-4xl md:text-5xl lg:text-6xl"
                : "text-2xl md:text-3xl lg:text-4xl"
            }`}
            style={{ fontWeight: 400 }}
          >
            {job.title}
          </h3>
          <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 font-mono-sm text-[#FFFFFF]/75">
            <span className="inline-flex items-center gap-2">
              <MapPin className="w-3 h-3 text-[#DA7B3C]" strokeWidth={2} />
              {job.area}
            </span>
            <span className="text-[#DA7B3C]">·</span>
            <span>{job.year}</span>
          </div>
        </motion.div>
      </div>

      {/* Hover arrow */}
      <motion.div
        variants={{
          rest: { opacity: 0.6, rotate: 0 },
          hover: { opacity: 1, rotate: 12 },
        }}
        transition={{ duration: 0.4 }}
        className="absolute top-5 md:top-16 right-5 md:right-5 z-10 flex items-center justify-center w-10 h-10 md:w-11 md:h-11 rounded-full border border-[#FFFFFF]/50 group-hover:border-[#DA7B3C] group-hover:bg-[#DA7B3C]"
      >
        <ArrowUpRight className="w-4 h-4 text-[#FFFFFF]" strokeWidth={2} />
      </motion.div>
    </motion.a>
  );
}

export default function Gallery() {
  return (
    <section
      id="work"
      data-testid="gallery-section"
      className="py-28 md:py-40 bg-[#F5F3EE] text-[#111613]"
    >
      <div className="max-w-[1440px] mx-auto px-5 sm:px-8 lg:px-12">
        <FadeUp>
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-16 md:mb-20 items-end">
            <div className="md:col-span-8">
              <div className="chapter-line text-[#0E2A1A] mb-6">
                Ch. 02 · Our Work
              </div>
              <h2
                className="font-display text-[#0E2A1A] text-6xl sm:text-7xl md:text-8xl lg:text-[7.5rem] leading-[0.92] tracking-[-0.03em]"
                style={{ fontWeight: 400 }}
              >
                Yards we're
                <br />
                <span className="italic text-[#DA7B3C]">proud of.</span>
              </h2>
            </div>
            <p className="md:col-span-4 text-lg text-[#111613]/70 leading-relaxed max-w-sm">
              Real jobs around San Antonio — mulch beds, fresh sod, rebuilt
              lawns. Every one signed off by a happy neighbor.
            </p>
          </div>
        </FadeUp>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-5 auto-rows-[360px]">
          {JOBS.map((job, i) => (
            <FadeUp
              key={job.id}
              delay={i * 0.08}
              className={`${job.span} h-full`}
            >
              <JobCard
                job={job}
                index={i}
                big={job.span.includes("row-span-2")}
              />
            </FadeUp>
          ))}
        </div>

        <FadeUp delay={0.2}>
          <div className="mt-14 flex flex-col sm:flex-row items-center justify-between gap-6 pt-8 border-t border-[#111613]/20">
            <p className="text-sm text-[#111613]/70 max-w-xl">
              Want your yard featured here? Book a job and we'll add the photos
              (with your permission) to the gallery.
            </p>
            <a href="#quote" data-testid="gallery-cta" className="btn-primary">
              Start Your Project
              <ArrowUpRight className="w-4 h-4" strokeWidth={2} />
            </a>
          </div>
        </FadeUp>
      </div>
    </section>
  );
}
